import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { DivisionDiscount, PagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save division discount data.
 */
@Injectable()
export class DivisionDiscountService {
    public static INCLUDE_DIVISIONS: string = 'divisions';

    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all division discounts for the specified event
     */
    public getIndex(eventId: string, includes: string[] = null): Observable<DivisionDiscount[]> {
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const bodyData: any = response.data || [];
                const divisionDiscounts: DivisionDiscount[] = [];
                for (const division of bodyData) {
                    divisionDiscounts.push((new DivisionDiscount()).deserialize(division, response.included));
                }

                return divisionDiscounts;
            }));
    }

    public getIndexListResponse(
        orderId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<PagedResponse> {
        const apiRequestUrl: string = this.getApiUrl(orderId, null, includes, queryParams);
        return this.getIndexListResponseByUrl(apiRequestUrl);
    }

    public getIndexListResponseByUrl(url: string): Observable<PagedResponse> {
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;
                const included: any[] = response.included;

                const orderItems: DivisionDiscount[] = [];
                for (const orderItem of json.data) {
                    orderItems.push((new DivisionDiscount()).deserialize(orderItem, included));
                }

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    orderItems,
                );
            }));
    }

    /**
     * Get a single division discount's data
     */
    public get(eventId: string, divisionDiscountId: string, includes: string[]): Observable<DivisionDiscount> {
        const apiRequestUrl: string = this.getApiUrl(eventId, divisionDiscountId, includes);

        return this.http.get(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => (new DivisionDiscount().deserialize(response, response.included))),
            );
    }

    /**
     * Add or update the division discount
     */
    public save(divisionDiscount: DivisionDiscount, includes: string[] = null): Observable<DivisionDiscount> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(divisionDiscount.eventId, divisionDiscount.id, includes);

        if (divisionDiscount.id) {
            // Update existing DivisionDiscount
            response = this.http.patch(apiRequestUrl, divisionDiscount.serialize());
        } else {
            // Add a new DivisionDiscount
            response = this.http.post(apiRequestUrl, divisionDiscount.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new DivisionDiscount()).deserialize(response, response.included)),
            );
    }

    /**
     * Delete the specified division discount
     */
    public delete(eventId: string, divisionDiscountId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, divisionDiscountId, null);

        return this.http.delete(apiRequestUrl).pipe(map(() => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, divisionDiscountId: string, includes: string[], queryParams: string[] = null)
    : string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/division-discounts/`;

        if (divisionDiscountId) {
            url += divisionDiscountId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
